import type { components } from '../../api/tipos'

export type EntradaResolver = components['schemas']['EntradaResolver']
export type ExtraPedido = components['schemas']['ExtraPedido']

/**
 * El analisis de la lista que se pega en el modo masivo.
 *
 * Es lo unico de la pantalla que no puede delegarse en el backend: `/resolver`
 * recibe columnas ya separadas, asi que alguien tiene que decidir donde termina
 * el NIF y empieza el bono. Se hace aqui, en una funcion pura, para que la
 * regla del diseno -"una linea que no se puede interpretar no bloquea a las
 * demas"- sea comprobable sin montar la pantalla.
 */

export interface LineaLista {
  /** Numero de la linea dentro del texto pegado, empezando por 1. */
  numero: number
  /** El texto original. Se guarda para poder ensenar lo que no se entendio. */
  texto: string
  nif: string
  bono?: number
  litraje?: string
  extras: ExtraPedido[]
  /** Por que esta linea no se puede resolver. Sin esto, la linea es valida. */
  error?: string
}

/**
 * El mismo criterio que `normalizar_nif` del backend, que a su vez copia el del
 * workflow de n8n en produccion. Si aqui se normalizara distinto, la pantalla
 * consideraria repetidos NIF que el backend ve como distintos, y al reves.
 */
export function normalizarNif(valor: string): string {
  return valor.replace(/[ \-.]/g, '').trim().toUpperCase()
}

/**
 * Las primeras columnas de una cabecera pegada desde Excel. No hay NIF real que
 * se llame asi, de modo que ignorarlas no puede tragarse un cliente.
 */
const CABECERAS = new Set(['NIF', 'CIF', 'DNI', 'CODE', 'CODIGO', 'CLIENTE'])

/** Una `L` suelta detras del litraje: `18,9 L` escrito separando con espacios. */
const SOLO_LITROS = /^l(itros?)?$/i

const ENTERO = /^-?\d+$/

/**
 * Que separa las columnas. El orden importa y no es caprichoso: la coma es
 * ademas el separador decimal espanol, asi que solo se usa como delimitador
 * cuando no hay ni tabulador ni punto y coma. Es la misma renuncia que hace
 * cualquier CSV: con la coma de delimitador, el litraje se escribe `18.9`.
 * Con tabulador, punto y coma o espacios, `18,9` sigue valiendo.
 */
function separar(linea: string): string[] {
  const trozos = linea.includes('\t')
    ? linea.split('\t')
    : linea.includes(';')
      ? linea.split(';')
      : linea.includes(',')
        ? linea.split(',')
        : linea.split(/\s+/)

  const campos = trozos.map((trozo) => trozo.trim())
  // Solo se quitan los vacios del final (una columna de extras sin rellenar).
  // Un hueco interior es informacion: `NIF\t\t18.9` es un bono que falta, y
  // descartarlo convertiria el litraje en bono.
  while (campos.length > 0 && campos[campos.length - 1] === '') campos.pop()
  return campos
}

/** `20.0` -> `20`, `18.90` -> `18.9`, igual que `_sin_ceros` del backend. */
function sinCeros(texto: string): string {
  return texto.includes('.') ? texto.replace(/0+$/, '').replace(/\.$/, '') : texto
}

function leerLitraje(valor: string): string | null {
  const texto = valor.replace(/,/g, '.').replace(/\s+/g, '').toUpperCase()
  const numero = texto.endsWith('L') ? texto.slice(0, -1) : texto
  if (numero === '' || !Number.isFinite(Number(numero))) return null
  return sinCeros(numero)
}

/**
 * La cantidad de un producto, escrita detras y con `x`: `OROTANA 18.9L x 2`.
 *
 * Detras y no delante para no chocar con el bono: una linea que empieza por un
 * numero **es** un bono, y esa lectura no se puede tocar sin cambiarle el
 * significado a las listas que ya se usan. Admite negativos, que es como va la
 * devolucion de envases.
 */
const CANTIDAD_AL_FINAL = /\s+x\s*(-?\d+(?:[.,]\d+)?)\s*$/i

/**
 * Los productos van todos en la ultima columna y se separan con `+`, para que
 * el nombre pueda llevar espacios ("dispensador de sobremesa") aunque la linea
 * entera se haya escrito separando con espacios.
 */
function leerExtras(campos: string[]): ExtraPedido[] {
  return campos
    .join(' ')
    .split('+')
    .map((trozo) => trozo.trim())
    .filter((trozo) => trozo !== '')
    .map((trozo) => {
      const conCantidad = CANTIDAD_AL_FINAL.exec(trozo)
      if (conCantidad === null) return { nombre: trozo, cantidad: 1 }
      return {
        nombre: trozo.slice(0, conCantidad.index).trim(),
        cantidad: Number(conCantidad[1].replace(',', '.')),
      }
    })
    .filter((extra) => extra.nombre !== '' && extra.cantidad !== 0)
}

function analizarLinea(texto: string, numero: number): LineaLista {
  const campos = separar(texto)
  const nif = campos[0] ?? ''
  const base = { numero, texto, nif, extras: [] as ExtraPedido[] }

  if (nif === '') return { ...base, error: 'Esta linea no trae NIF, no hay por donde buscar.' }

  const bonoTexto = campos[1] ?? ''
  const resto = campos.slice(1).filter((campo) => campo !== '')
  if (resto.length === 0) {
    return {
      ...base,
      error: 'Esta linea solo trae el NIF: falta el bono o los productos que se reparten.',
    }
  }

  // Una linea que no empieza por un numero **no es un bono**: es una lista de
  // productos. 114 de los 478 pedidos reales van asi (garrafa suelta, packs,
  // dispensadores), y sin esta rama no habia forma de pegarlos.
  if (!ENTERO.test(bonoTexto)) {
    const extras = leerExtras(resto)
    if (extras.length === 0) {
      return { ...base, error: `No se entiende '${resto.join(' ')}' como una lista de productos.` }
    }
    return { ...base, extras }
  }

  const bono = Number(bonoTexto)
  if (bono <= 0) return { ...base, error: `Un bono de ${bono} garrafas no es un pedido.` }

  const litrajeTexto = campos[2] ?? ''
  if (litrajeTexto === '') {
    return { ...base, bono, error: 'Falta el litraje: 18.9, 12.8, 20...' }
  }
  const litraje = leerLitraje(litrajeTexto)
  if (litraje === null) {
    return {
      ...base,
      bono,
      error: `No se entiende el litraje '${litrajeTexto}'. Escribelo como 18.9 o 18,9 L.`,
    }
  }

  // `18,9 L` separado con espacios deja la unidad en su propia columna.
  const productos = SOLO_LITROS.test(campos[3] ?? '') ? campos.slice(4) : campos.slice(3)

  return { ...base, bono, litraje, extras: leerExtras(productos) }
}

/**
 * Convierte el texto pegado en una linea por cliente, en el mismo orden.
 *
 * Las lineas en blanco desaparecen (no son un error de nadie) y la cabecera de
 * Excel tambien. Todo lo demas sale en el resultado, con o sin `error`: la
 * revision tiene que ensenar tambien lo que no se ha entendido, o el
 * administrador publicaria diecinueve de veinte clientes sin enterarse.
 */
export function analizarLista(texto: string): LineaLista[] {
  const lineas: LineaLista[] = []
  const vistos = new Map<string, number>()
  let primera = true

  texto.split(/\r?\n/).forEach((cruda, indice) => {
    const limpia = cruda.trim()
    if (limpia === '') return

    if (primera) {
      primera = false
      if (CABECERAS.has((separar(limpia)[0] ?? '').toUpperCase())) return
    }

    const linea = analizarLinea(limpia, indice + 1)
    const clave = normalizarNif(linea.nif)

    // Dos lineas del mismo cliente para el mismo dia son dos albaranes a la
    // misma puerta. El backend lo pararia con la idempotencia, pero para
    // entonces el primero ya estaria creado y el segundo diria "duplicado" sin
    // que nadie sepa cual de los dos bonos se ha publicado.
    const anterior = linea.error === undefined && clave !== '' ? vistos.get(clave) : undefined
    if (anterior !== undefined) {
      lineas.push({ ...linea, error: `El NIF ${clave} ya aparece en la linea ${anterior}.` })
      return
    }
    if (linea.error === undefined && clave !== '') vistos.set(clave, linea.numero)
    lineas.push(linea)
  })

  return lineas
}

/** Lo que se manda a `POST /resolver`: solo las lineas que se han entendido. */
export function aEntradas(lineas: LineaLista[]): EntradaResolver[] {
  return lineas
    .filter((linea) => linea.error === undefined)
    .map((linea) => ({
      nif: normalizarNif(linea.nif),
      bono: linea.bono,
      litraje: linea.litraje,
      variante: '',
      extras: linea.extras,
    }))
}
